import clsx from "clsx";

import { BrandIcon } from "@shared/ui/brand-icon";
import { ContactLink } from "@shared/ui/contact-link";
import { SocialIcon } from "@shared/ui/social-icon";
import { Body } from "@shared/ui/typography";
import { contactUtils } from "@shared/utils";

import s from "./map-details.module.scss";

export type WorkTimeListItem = {
  id?: string | number;
  timeSlot: string;
};

export type MapDetailsSocialLink = {
  name: string;
  url?: string | null;
};

export type MapDetailsProps = {
  className?: string;
  location?: string;
  locationSuffix?: string;
  distanceFromResort?: string;
  workTimeList?: WorkTimeListItem[];
  workingHours?: string;
  phone?: string;
  emails?: Array<string | null | undefined>;
  socialLinks?: MapDetailsSocialLink[];
  linkifyContacts?: boolean;
  formatPhone?: boolean;
  showContactIcons?: boolean;
  showWorkingHoursIcon?: boolean;
};

const getDisplayLocation = (location?: string, suffix?: string) => {
  if (!location) return "";
  return suffix ? `${location}, ${suffix}` : location;
};

export const MapDetails = (props: MapDetailsProps) => {
  const {
    className,
    location,
    locationSuffix,
    distanceFromResort,
    workTimeList,
    workingHours,
    phone,
    emails,
    socialLinks,
    linkifyContacts = true,
    formatPhone = false,
    showContactIcons = false,
    showWorkingHoursIcon = false,
  } = props;

  const displayLocation = getDisplayLocation(location, locationSuffix);
  const normalizedEmails = (emails || []).filter((email): email is string =>
    Boolean(email),
  );
  const hasWorkTimeList = Boolean(workTimeList && workTimeList.length > 0);
  const hasEmails = normalizedEmails.length > 0;
  const hasPhone = Boolean(phone);
  const hasWorkingHoursInContactsGroup = Boolean(
    workingHours && !showWorkingHoursIcon,
  );
  const hasWorkTimeListInContactsGroup = Boolean(
    hasWorkTimeList && !showWorkingHoursIcon,
  );

  const hasContactsWithIcons = Boolean(
    showContactIcons && (hasEmails || hasPhone),
  );
  const hasContactsInContactsGroup = Boolean(
    (!showContactIcons && (hasEmails || hasPhone)) ||
      hasWorkingHoursInContactsGroup ||
      hasWorkTimeListInContactsGroup,
  );
  const hasSocialLinks = Boolean(socialLinks && socialLinks.length > 0);

  const renderContactValue = (
    value: string,
    type: "email" | "phone",
    displayValue: string,
  ) => {
    if (!linkifyContacts) {
      return (
        <Body size="uppercase" weight="semiBold">
          {displayValue}
        </Body>
      );
    }

    return (
      <ContactLink type={type} value={value} className="global-link">
        <Body size="uppercase" weight="semiBold">
          {displayValue}
        </Body>
      </ContactLink>
    );
  };

  return (
    <div className={clsx(s.root, className)}>
      {displayLocation && (
        <div className={s.row}>
          <BrandIcon
            name="location"
            fillColor="cadmium"
            strokeColor="white"
            className={s.icon}
          />
          <Body size="uppercase" weight="semiBold">
            {displayLocation}
          </Body>
        </div>
      )}

      {distanceFromResort && (
        <div className={s.row}>
          <BrandIcon
            name="map"
            fillColor="violet"
            strokeColor="white"
            className={s.icon}
          />
          <Body size="uppercase" weight="semiBold">
            {distanceFromResort}
          </Body>
        </div>
      )}

      {hasWorkTimeList && showWorkingHoursIcon && (
        <div className={s.row}>
          <BrandIcon
            name="clock"
            fillColor="gold"
            strokeColor="white"
            className={s.icon}
          />
          <div className={s.list}>
            {workTimeList?.map((item, idx) =>
              item?.timeSlot != null && item.timeSlot !== "" ? (
                <Body
                  key={item?.id ?? item?.timeSlot ?? idx}
                  size="uppercase"
                  weight="semiBold"
                >
                  {item.timeSlot}
                </Body>
              ) : null,
            )}
          </div>
        </div>
      )}

      {workingHours && showWorkingHoursIcon && (
        <div className={s.row}>
          <BrandIcon
            name="clock"
            fillColor="gold"
            strokeColor="white"
            className={s.icon}
          />
          <Body size="uppercase" weight="semiBold">
            {workingHours}
          </Body>
        </div>
      )}

      {hasContactsWithIcons && (
        <div className={s.contacts}>
          {normalizedEmails.map((email) => (
            <div key={email} className={s.row}>
              <BrandIcon
                name="link"
                fillColor="azure"
                strokeColor="white"
                className={s.icon}
              />
              {renderContactValue(email, "email", email)}
            </div>
          ))}

          {phone && (
            <div className={s.row}>
              <BrandIcon
                name="phone"
                fillColor="violet"
                strokeColor="white"
                className={s.icon}
              />
              {renderContactValue(
                phone,
                "phone",
                formatPhone
                  ? contactUtils.formatPhoneForDisplay(phone, "brackets")
                  : phone,
              )}
            </div>
          )}
        </div>
      )}

      {(hasContactsInContactsGroup || hasSocialLinks) && (
        <div className={s.contactsGroup}>
          {hasContactsInContactsGroup && (
            <div className={s.contacts}>
              {hasWorkingHoursInContactsGroup && (
                <Body size="uppercase" weight="semiBold">
                  {workingHours}
                </Body>
              )}

              {hasWorkTimeListInContactsGroup && workTimeList && (
                <div className={s.list}>
                  {workTimeList.map((item) => (
                    <Body
                      key={item.id || item.timeSlot}
                      size="uppercase"
                      weight="semiBold"
                    >
                      {item.timeSlot}
                    </Body>
                  ))}
                </div>
              )}

              {phone && !showContactIcons && (
                <div>
                  {renderContactValue(
                    phone,
                    "phone",
                    formatPhone
                      ? contactUtils.formatPhoneForDisplay(phone, "brackets")
                      : phone,
                  )}
                </div>
              )}

              {!showContactIcons &&
                normalizedEmails.map((email) => (
                  <div key={email}>
                    {renderContactValue(email, "email", email)}
                  </div>
                ))}
            </div>
          )}

          {hasSocialLinks && (
            <div className={s.socialMedia}>
              {socialLinks?.map((social, idx) =>
                social?.name ? (
                  <SocialIcon
                    key={social.name ?? idx}
                    url={social?.url ?? "#"}
                    platform={social.name}
                    isWhite
                  />
                ) : null,
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

MapDetails.displayName = "MapDetails";
